import BuildProperties from "@/envconfig/BuildProperties";
import { Values } from "@/constants/Values";
import store from "@/store/index";
import axios, { InternalAxiosRequestConfig } from "axios";
import { setisLoading } from "@/store/slices/global.Slice";
import { toast as headerToast } from "@/components/ui/use-toast";
import { toast } from "@/components/ui/sonner";

import Strings from "@/constants/String";
import { forceLogout } from "@/lib/forceLogout";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
export const ApiMethods = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
};

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  noAuth?: boolean;
  showLoader?: boolean;
  useDBLToken?:boolean;
  customToken?:string
}

const API_BASE_URL = BuildProperties.getBaseUrl();

export const axiosIns = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosIns.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    if(config?.customToken){
      config.headers.Authorization = config.customToken;
    }else{
      let token=sessionStorage.getItem(Values.jwtTokenV2);
      if (token && !config.noAuth) {
        config.headers.Authorization = token;
      }
      if (config.method === "get" && config.showLoader) {
        store.dispatch(showLoader())
      }
    }
    return config;
  },
  (error) => {
    store.dispatch(hideLoader())
    return Promise.reject(error);
  }
);

axiosIns.interceptors.response.use(
  (response) => {
    store.dispatch(hideLoader())
    return response;
  },
  (error) => {
    // If token expired, logout user and redirect to login page
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      // reset the navigation state to login page
      forceLogout()
      store.dispatch(hideLoader())
      return Promise.reject(error);
    }
    store.dispatch(hideLoader())
    return Promise.reject(error);
  }
);

export const AxiosClient = async ({
  method,
  url,
  headers = null,
  data = null,
  pathParam = null,
  showCustomError = true,
  hideSpinnerAfterApicall = true,
  isEncrypted = BuildProperties.isProdEnv() ? true : false,
  noAuth = false,
  showLoader = true,
  showFooterToast = false,
  showHeaderToast = false,
  showSuccessMessage= false,
  isFormData = false,
  useDBLToken= false, // 👈 NEW
  customToken=null

}) => {

  try {
  

    if ((method === ApiMethods.GET || method === ApiMethods.POST || method === ApiMethods.PUT) && pathParam) {
      url = `${url}/${pathParam}`
    }

      // If GET request, add data to URL
      if (method === ApiMethods.GET && data) {
        let urlParams = "";
        Object.keys(data).forEach((key, index) => {
          urlParams += (index === 0 ? "?" : "&") + `${key}=${data[key]}`;
        });
        url += `${urlParams}`;
      }

    let reqheaders = {
      "Content-Type": "application/json",
      ...headers,
    };
    if (isFormData) {
      reqheaders = {
        "Content-Type": "multipart/form-data",
        ...headers,
      }
    }
    const response = await axiosIns({
      url: url,
      method: method,
      headers: reqheaders,
      data: method === ApiMethods.GET ? null : data,
      timeout: Values.API_TIMEOUT * 1000, // 60 seconds
      noAuth,
      showLoader,
      customToken
    } as CustomAxiosRequestConfig);

    if (response?.data?.error) {
      if (hideSpinnerAfterApicall) {
        store?.dispatch(setisLoading(false));
      }
    if(showCustomError){
      // toast.error(`${response?.data?.error?.message}`, { closeButton: true });

    }

      return Promise.reject({
        error: response?.data?.error,
        success: response?.data?.success || false,
      });
    } else {
      if (hideSpinnerAfterApicall) {
        store?.dispatch(setisLoading(false));
      }
      const responseData = response.data;
      if (showSuccessMessage && responseData?.message ) {
      
          toast.success(`${responseData?.message}`, { closeButton: true });
      }
      return responseData;
    }
  } catch (error) {
    // TODO: Handle error based on the use case
    // showing custom error message to user in toast
    if (hideSpinnerAfterApicall) {
      store?.dispatch(setisLoading(false));
    }

    if (error.status === 403) {
      sessionStorage.removeItem("bearer_token")
    }

    if (showCustomError) {
      toast.error(error?.response?.data?.error ?
        error?.response?.data?.error :
        error?.response?.data?.message ?
          error?.response?.data?.message :
          error?.response?.status === 401 || error?.response?.status === 403
            ? Strings.LoginStrings.session_expired
            : error?.message,
        { closeButton: true }
      );
    }


    // return Promise.reject(error?.response?.data || error?.message);
    return Promise.reject({
      error: error?.response?.data?.error || error?.message,
      success: error?.response?.data?.success || false,
      errorInput: error?.response?.data?.errorInput || null
    });
  }
};

/**
 * How to use AxiosClient:
 * @description - AxiosClient is a wrapper around Axios to make API calls
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} url - API endpoint
 * @param {object} headers - Request headers (optional)
 * @param {object} data - Request body (optional)
 * @returns {object} - API response data
 * @throws {object} - Error response
 * @example
 * const response = await AxiosClient(ApiMethods.GET, '/users', {}, {id: 1});
 */
